import { HttpEventType, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Params } from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ISetValueDocument,
  IFormDocument,
  TSetValueType,
  IControl,
  isControlCell,
  IListElementControlCell,
  getCellControl,
} from 'app/services/api5-service/api.interface';
import { DOC_CONTEXT } from 'app/services/doc-context/context-token';
import { DocContextService } from 'app/services/doc-context/context.service';
import { setTabUploadPreloaderFromEvent } from 'app/services/http-base-service/handlers/handler.utils';
import {
  BehaviorSubject,
  EMPTY,
  firstValueFrom,
  interval,
  Observable,
  Subject,
  Subscription,
} from 'rxjs';
import {
  debounceTime,
  delay,
  filter,
  finalize,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';

import { Queue } from 'common/queue/queue.class';
import { mergeDocs } from 'utils/doc-merge';
import { DocHelper } from 'utils/dochelper';
import { filterNotDefinedItems } from 'utils/filter-not-defined-items';
import { hasFile } from 'utils/has-file';

import { FormService } from './form.service';
import { HiddenService } from './hidden.service';

import {
  Api5Service,
  SetValuesParams,
} from '../../../app/services/api5-service/api5.service';
import { IFormModel, ISetValueChanges } from '../dynamic-form.interface';
import { ISPFieldType } from '../model';
import { mapConfigs } from '../utils/formly-configs';
import { prepareFormModel } from '../utils/get-form-model';

/**
 * Set values handling event
 */
export interface SetValueStartEvent {
  type: 'start';
  /** Name of the field, that trigger set values */
  fieldName: string;
  /** Set values request params */
  params: SetValuesParams;
}

/**
 * Set values handling event
 */
export interface SetValueEndEvent {
  type: 'end';
  /** Name of the field, that trigger set values */
  fieldName: string;
  /** Set values request params */
  params: SetValuesParams;
  /** Set values result stream */
  result: ISetValueChanges;
  /** Show error */
  showError?: boolean;
}

export type SetValueEvent = SetValueEndEvent | SetValueStartEvent;

/**
 * Setting values service
 */
@UntilDestroy()
@Injectable()
export class SetValuesService {
  private readonly setValuesQueue = new Queue();

  /** Lock for skiping set values handling */
  private setValuesLock?: {
    /** Skip handling for this field */
    fieldName: string;
    /** Skip handling for all fields */
    final: boolean;
  };

  /**
   * Set values request event subject. Here you can catch setValues request for specific field
   */
  private readonly setValuesRequestEvent: Subject<SetValueEvent> =
    new Subject();

  /**
   * Flag to indicate some set values in progress
   */
  private readonly setValuesInProgressSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);

  readonly setValuesRequestEvent$ = this.setValuesRequestEvent.asObservable();

  /**
   * Check that some set values request is in progress
   */
  get setValuesInProgress(): boolean {
    return this.setValuesInProgressSubject.value;
  }

  /**
   * Get promise, that will be resolved, after all setValues is over
   */
  get setValuesOver(): Promise<void> {
    return firstValueFrom(
      this.setValuesInProgressSubject.pipe(
        // small delay in order to detect multiple set values requests in queue
        debounceTime(10),
        filter(inProgress => !inProgress),
        // small delay for set values applying to form model. Like select autopick logic and etc.
        delay(10),
        map(() => undefined),
      ),
    );
  }

  constructor(
    private readonly formService: FormService,
    private readonly hiddenService: HiddenService,
    private readonly api: Api5Service,
    @Optional()
    @Inject(DOC_CONTEXT)
    private readonly contextService?: DocContextService,
  ) {
    // subscribe to queue for sync execution
    this.setValuesQueue.execution$.pipe(untilDestroyed(this)).subscribe();
  }

  private isFinalSetValues(setValuesType: TSetValueType): boolean {
    return setValuesType === 'final' || setValuesType === 'finalblock';
  }

  private isBlockingSetValues(setValuesType: TSetValueType): boolean {
    return setValuesType === 'blocking' || setValuesType === 'finalblock';
  }

  /**
   * Apply setValues changes to form doc metadata
   *
   * @HACK this is required step for listReconfiguration. We mustn't change original doc, but in order to reconfigure formly configs
   * we need doc metadata. So we update doc meta, and then use it to reconfigure formly configs
   * @param changes - prepared setValues data
   */
  private applyDocUpdate(changes: ISetValueChanges): void {
    const doc = this.formService.loadedDoc;

    const newDoc = mergeDocs(doc, changes.setValuesDoc);

    if (changes.docToUpdate) {
      Object.entries(changes.docToUpdate).forEach(([key, value]) => {
        newDoc[key] = value;
      });
    }

    this.formService.setDoc(newDoc);
  }

  private getModelKeys(originalModel: IFormModel): string[] {
    return Object.keys(originalModel);
  }

  private getVisibleFieldsKeys(): string[] {
    return filterNotDefinedItems(
      mapConfigs(this.formService.configs, config => {
        const names: (string | undefined)[] = [];

        if (config.type === ISPFieldType.TextData) {
          const textDataKey = config.props.originalControl.$name;
          const imgKey = config.props.originalField.img?.[0].$name;
          names.push(textDataKey, imgKey);
        }

        if (config.type === ISPFieldType.TotalSum) {
          names.push(config.props.originalControl.$name);
        }

        if (config.props.originalField?.tag) {
          names.push(config.props.originalField.tag.$name);
        }

        return names;
      }).flat(),
    );
  }

  /**
   * Get control names of controls in all lists
   *
   * @param doc
   */
  private getListFieldsKeys(doc: ISetValueDocument): string[] {
    return (
      doc.list
        ?.map(list => {
          const elems = list.elem || [];
          const cells = elems.map(elem => Object.values(elem)).flat();
          const controlCells: IListElementControlCell[] = cells.filter(
            cellData => isControlCell(cellData),
          );
          const controlCellsControls = controlCells.map(
            cellData => getCellControl(cellData).control,
          );
          const controlCellsNames = controlCellsControls.map(
            control => control.$name,
          );

          return controlCellsNames;
        })
        .flat() || []
    );
  }

  /**
   * Picks up changed values from setValues document root and returns the flat model with changed values
   *
   * @param setValuesDoc - setValues document
   * @param originalModel - original form model before setValues
   */
  private getSetValuesChanges(
    setValuesDoc: ISetValueDocument,
    originalModel: IFormModel,
  ): ISetValueChanges {
    const changes: ISetValueChanges = {
      setValuesDoc,
    };

    // array of all field keys, that expected to be in setValues doc
    const docModelKeys = [
      ...this.getModelKeys(originalModel),
      ...this.getListFieldsKeys(setValuesDoc),
      ...this.getVisibleFieldsKeys(),
    ];

    changes.docToUpdate = docModelKeys.reduce<
      Partial<IFormDocument> | undefined
    >((valuesFromSetValuesDoc, key) => {
      // get new values from set-value doc. Used for doc merging. Required for lists reconfiguration
      // in reconfiguration process fields are created from scratch, and get their defaultValue from 'this.doc'
      // this is why we need to get those defaultValues from set-value doc
      const valueFromDoc = setValuesDoc[key];
      if (valueFromDoc) {
        if (valuesFromSetValuesDoc) {
          valuesFromSetValuesDoc[key] = valueFromDoc;
        } else {
          return {
            [key]: valueFromDoc,
          };
        }
      }

      return valuesFromSetValuesDoc;
    }, undefined);

    return changes;
  }

  /**
   * Prepare params for set values request
   *
   * @param params
   * @param params.fieldName - field. that trigger set values
   * @param params.additionalParams - additional params
   * @param params.sendFiles - should files be not be cut out from params
   * @param params.appendModel - append form model as request params
   */
  private prepareSetValuesParams(params: {
    fieldName: string;
    additionalParams?: Params;
    sendFiles?: boolean;
    appendModel?: boolean;
  }): SetValuesParams {
    const modelWithDisabledFields = params.appendModel
      ? this.formService.formGroup.getRawValue()
      : {};

    const requestParams = prepareFormModel({
      ...modelWithDisabledFields,
      ...params.additionalParams,
    });
    if (!params.sendFiles) {
      Object.entries(requestParams).forEach(([key, value]) => {
        if (value instanceof FileList) {
          delete requestParams[key];
        }
      });
    }

    const setValuesParams: SetValuesParams = {
      field: params.fieldName,
      params: requestParams,
      elid: DocHelper.elid(this.formService.doc),
      plid: DocHelper.plid(this.formService.doc),
      func: this.formService.loadedDoc.$func,
    };

    return setValuesParams;
  }

  /**
   * Send 'sv_field' request
   *
   * @param params
   */
  private requestSetValues(
    params: SetValuesParams,
  ): Observable<ISetValueDocument> {
    if (hasFile(params.params)) {
      return this.api.setValuesWithFiles(params).pipe(
        setTabUploadPreloaderFromEvent(
          this.formService.formState.context.fileUploadMsg,
          this.contextService?.context,
        ),
        filter(res => res.type !== HttpEventType.UploadProgress),
        map(res => (res as HttpResponse<ISetValueDocument>).body!),
      );
    } else {
      return this.api.setValues(params);
    }
  }

  /**
   * Check control metadata for interval setvalues. Chat case, for example
   *
   * @param control - control metadata
   */
  getIntervalSetValues$(
    control: IControl,
  ): Observable<ISetValueChanges> | null {
    let time = parseInt(control.$setvalues || '', 10);
    if (isNaN(time)) {
      return null;
    }

    const second = 1000;
    time = time * second;
    return interval(time).pipe(
      // sen interval request only if tab is active
      filter(() =>
        this.contextService ? this.contextService.context.isActive : true,
      ),
      switchMap(() => {
        const setValuesParams = this.prepareSetValuesParams({
          fieldName: control.$name,
          appendModel: true,
        });

        this.setValuesRequestEvent.next({
          type: 'start',
          fieldName: control.$name,
          params: setValuesParams,
        });

        return this.requestSetValues(setValuesParams).pipe(
          map(response => {
            return this.getSetValuesChanges(
              response,
              this.formService.formGroup.getRawValue(),
            );
          }),
          tap(changes => {
            if (changes) {
              this.setValuesRequestEvent.next({
                type: 'end',
                fieldName: control.$name,
                params: setValuesParams,
                result: changes,
              });
            }
          }),
        );
      }),
    );
  }

  /**
   * Handle set values event. Add set values to queue and treat result
   *
   * @param params - params for set values handling
   * @param params.setValuesType - setvalues type
   * @param params.fieldName - name of the field, that trigger setvalues
   * @param params.additionalParams - additional params for set values request
   * @param params.sendFiles - by default setvalues request doesn't send files. Define this option as true, if you want to send files
   * @param params.skipFinalLock - do not discard set values request by 'final' setvalues condition
   * @param params.bypassQueue - do not add request to queue
   * @param params.appendModel - append form model as request params
   * @param params.showError - show error
   */
  handleSetValues(params: {
    setValuesType: TSetValueType;
    fieldName: string;
    additionalParams?: Params;
    sendFiles?: boolean;
    skipFinalLock?: boolean;
    bypassQueue?: boolean;
    appendModel?: boolean;
    showError?: boolean;
  }): Observable<ISetValueChanges> {
    const isNumberSetValues = !isNaN(parseInt(params.setValuesType, 10));
    // number set values should be treated other way!
    if (isNumberSetValues) {
      return EMPTY;
    }

    // should set values request be discarded by final condition or self triggering
    const selfTriggering = this.setValuesLock?.fieldName === params.fieldName;
    const finalLock = this.setValuesLock?.final && !params.skipFinalLock;
    const shouldSkipHandling = selfTriggering || finalLock;
    if (shouldSkipHandling) {
      return EMPTY;
    }

    const isFinal = this.isFinalSetValues(params.setValuesType);
    const isBlocking = this.isBlockingSetValues(params.setValuesType);
    const setValuesParams = this.prepareSetValuesParams(params);

    const request = this.requestSetValues(setValuesParams).pipe(
      map(response =>
        this.getSetValuesChanges(
          response,
          this.formService.formGroup.getRawValue(),
        ),
      ),
      tap(() => {
        // set lock in order to NOT trigger set values requests while applying changes to form
        this.setValuesLock = {
          fieldName: params.fieldName,
          final: isFinal,
        };
      }),
      tap(changes => {
        this.applyDocUpdate(changes);

        this.setValuesRequestEvent.next({
          type: 'end',
          fieldName: params.fieldName,
          params: setValuesParams,
          result: changes,
          showError: params.showError,
        });
      }),
      finalize(() => {
        // only those who set blocking preloader can remove it
        if (isBlocking && this.contextService) {
          // remove blocking preloader
          this.contextService.context.setPreloader(false);
        }
        // remove lock for further requests
        delete this.setValuesLock;
      }),
    );

    const setValuesRequest = new Observable<ISetValueChanges>(observer => {
      if (isBlocking && this.contextService) {
        // push blocking event to trigger preloader appearing
        this.contextService.context.setPreloader(true);
      }

      const subscription = request.subscribe(observer);

      this.setValuesRequestEvent.next({
        type: 'start',
        fieldName: params.fieldName,
        params: setValuesParams,
      });

      return () => subscription.unsubscribe();
    });

    return new Observable(observer => {
      this.setValuesInProgressSubject.next(true);

      let sub: Subscription;
      if (params.bypassQueue) {
        sub = setValuesRequest
          .pipe(finalize(() => this.setValuesInProgressSubject.next(false)))
          .subscribe(observer);
      } else {
        sub = this.setValuesQueue
          .add$(setValuesRequest)
          .pipe(finalize(() => this.setValuesInProgressSubject.next(false)))
          .subscribe(observer);
      }

      return () => sub.unsubscribe();
    });
  }
}
